import { Injectable } from '@angular/core';
import { CandidatStatusOnMission } from '../shared/utils/enums';
import { Mission, AdresseDto } from '../models/Mission';

@Injectable({
  providedIn: 'root',
})
export class AnonymizationService {
  constructor() {}

  /**
   * Determines if a mission should be anonymized based on the candidate's status
   */
  shouldAnonymize(statusOfCandidatOnMission: string): boolean {
    // Anonymize if the contract is not signed
    return !(
      statusOfCandidatOnMission === CandidatStatusOnMission.ACCEPTED_AND_SIGNED_BY_BOTH ||
      statusOfCandidatOnMission === CandidatStatusOnMission.REJECTED ||
      statusOfCandidatOnMission === CandidatStatusOnMission.CANCELED_BY_CANDIDATE ||
      statusOfCandidatOnMission === CandidatStatusOnMission.MISSION_VALIDATED_WAITING_FOR_PAYMENT ||
      statusOfCandidatOnMission === CandidatStatusOnMission.PAYMENT_DONE
    );
  }

  /**
   * Anonymizes a mission object if needed based on the candidate's status
   */
  anonymizeMissionIfNeeded(mission: any): any {
    if (!mission) return mission;

    // Create a copy of the mission to avoid modifying the original
    const missionCopy = { ...mission };

    // Check if anonymization is needed
    if (this.shouldAnonymize(missionCopy.statusOfCandidatOnMission)) {
      // Anonymize establishment name
      const isPublic = missionCopy.etablissementDto?.typeetablissement == 'CP-MCO';
      const departmentName = missionCopy.adresseDto?.departmentName || '';
      const departmentCode = this.mapDepartmentNameToCode(missionCopy.adresseDto?.departmentName) || '';
      const departmentInfo = departmentName && departmentCode ? `${departmentName} (${departmentCode})` : 'N/A';
      
      // Anonymize establishment name
      missionCopy.etablissementNom = `Etablissement ${isPublic ? 'public' : 'privé'} dans ${departmentInfo}`;
      
      // Anonymize site name
      if (missionCopy.adresseDto) {
        const adresseCopy = { ...missionCopy.adresseDto };
        adresseCopy.name = adresseCopy.issocial ? 'Principal' : 'Secondaire';
        
        // Anonymize address
        adresseCopy.adress = `${departmentInfo}`;
        
        // Clear map coordinates
        // adresseCopy.lat = null;
        // adresseCopy.lon = null;
        
        missionCopy.adresseDto = adresseCopy;
      }
      
      missionCopy.serviceAffectationsName = missionCopy.serviceType
      
      // Replace photos with generic ones
      const genericServicePhoto = '/assets/images/generic-service.png'; // Update with actual path
      missionCopy.photo1 = genericServicePhoto;
      missionCopy.photo2 = null;
      missionCopy.photo3 = null;
      missionCopy.photo4 = null;
      missionCopy.photo5 = null;
      
      if (missionCopy.adresseDto) {
        missionCopy.adresseDto.firstPhoto = genericServicePhoto;
        missionCopy.adresseDto.secondPhoto = null;
      }
      
      // If the mission has images array (used in some components)
      if (missionCopy.images && Array.isArray(missionCopy.images)) {
        missionCopy.images = [genericServicePhoto];
      }
    }
    
    return missionCopy;
  }

  /**
   * Anonymizes an array of missions if needed
   */
  anonymizeMissionsIfNeeded(missions: any[]): any[] {
    if (!missions || !Array.isArray(missions)) return missions;
    
    return missions.map(mission => this.anonymizeMissionIfNeeded(mission));
  }

  mapDepartmentNameToCode(departmentName: string): string {
    const departments = [
      { code: '01', name: 'Ain' },
      { code: '02', name: 'Aisne' },
      { code: '03', name: 'Allier' },
      { code: '04', name: 'Alpes-de-Haute-Provence' },
      { code: '05', name: 'Hautes-Alpes' },
      { code: '06', name: 'Alpes-Maritimes' },
      { code: '07', name: 'Ardèche' },
      { code: '08', name: 'Ardennes' },
      { code: '09', name: 'Ariège' },
      { code: '10', name: 'Aube' },
      { code: '11', name: 'Aude' },
      { code: '12', name: 'Aveyron' },
      { code: '13', name: 'Bouches-du-Rhône' },
      { code: '14', name: 'Calvados' },
      { code: '15', name: 'Cantal' },
      { code: '16', name: 'Charente' },
      { code: '17', name: 'Charente-Maritime' },
      { code: '18', name: 'Cher' },
      { code: '19', name: 'Corrèze' },
      { code: '2A', name: 'Corse-du-Sud' },
      { code: '2B', name: 'Haute-Corse' },
      { code: '21', name: "Côte-d'Or" },
      { code: '22', name: "Côtes d'Armor" },
      { code: '23', name: 'Creuse' },
      { code: '24', name: 'Dordogne' },
      { code: '25', name: 'Doubs' },
      { code: '26', name: 'Drôme' },
      { code: '27', name: 'Eure' },
      { code: '28', name: 'Eure-et-Loir' },
      { code: '29', name: 'Finistère' },
      { code: '30', name: 'Gard' },
      { code: '31', name: 'Haute-Garonne' },
      { code: '32', name: 'Gers' },
      { code: '33', name: 'Gironde' },
      { code: '34', name: 'Hérault' },
      { code: '35', name: 'Ille-et-Vilaine' },
      { code: '36', name: 'Indre' },
      { code: '37', name: 'Indre-et-Loire' },
      { code: '38', name: 'Isère' },
      { code: '39', name: 'Jura' },
      { code: '40', name: 'Landes' },
      { code: '41', name: 'Loir-et-Cher' },
      { code: '42', name: 'Loire' },
      { code: '43', name: 'Haute-Loire' },
      { code: '44', name: 'Loire-Atlantique' },
      { code: '45', name: 'Loiret' },
      { code: '46', name: 'Lot' },
      { code: '47', name: 'Lot-et-Garonne' },
      { code: '48', name: 'Lozère' },
      { code: '49', name: 'Maine-et-Loire' },
      { code: '50', name: 'Manche' },
      { code: '51', name: 'Marne' },
      { code: '52', name: 'Haute-Marne' },
      { code: '53', name: 'Mayenne' },
      { code: '54', name: 'Meurthe-et-Moselle' },
      { code: '55', name: 'Meuse' },
      { code: '56', name: 'Morbihan' },
      { code: '57', name: 'Moselle' },
      { code: '58', name: 'Nièvre' },
      { code: '59', name: 'Nord' },
      { code: '60', name: 'Oise' },
      { code: '61', name: 'Orne' },
      { code: '62', name: 'Pas-de-Calais' },
      { code: '63', name: 'Puy-de-Dôme' },
      { code: '64', name: 'Pyrénées-Atlantiques' },
      { code: '65', name: 'Hautes-Pyrénées' },
      { code: '66', name: 'Pyrénées-Orientales' },
      { code: '67', name: 'Bas-Rhin' },
      { code: '68', name: 'Haut-Rhin' },
      { code: '69', name: 'Rhône' },
      { code: '70', name: 'Haute-Saône' },
      { code: '71', name: 'Saône-et-Loire' },
      { code: '72', name: 'Sarthe' },
      { code: '73', name: 'Savoie' },
      { code: '74', name: 'Haute-Savoie' },
      { code: '75', name: 'Paris' },
      { code: '76', name: 'Seine-Maritime' },
      { code: '77', name: 'Seine-et-Marne' },
      { code: '78', name: 'Yvelines' },
      { code: '79', name: 'Deux-Sèvres' },
      { code: '80', name: 'Somme' },
      { code: '81', name: 'Tarn' },
      { code: '82', name: 'Tarn-et-Garonne' },
      { code: '83', name: 'Var' },
      { code: '84', name: 'Vaucluse' },
      { code: '85', name: 'Vendée' },
      { code: '86', name: 'Vienne' },
      { code: '87', name: 'Haute-Vienne' },
      { code: '88', name: 'Vosges' },
      { code: '89', name: 'Yonne' },
      { code: '90', name: 'Territoire-de-Belfort' },
      { code: '91', name: 'Essonne' },
      { code: '92', name: 'Hauts-de-Seine' },
      { code: '93', name: 'Seine-Saint-Denis' },
      { code: '94', name: 'Val-de-Marne' },
      { code: '95', name: "Val-D'Oise" },
      { code: '971', name: 'Guadeloupe' },
      { code: '972', name: 'Martinique' },
      { code: '973', name: 'Guyane' },
      { code: '974', name: 'La Réunion' },
      { code: '976', name: 'Mayotte' },
    ];
    const department = departments.find(
      (department) => department.name === departmentName
    );
    return department?.code || 'N/A';
  }
}